import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import React, { useState } from 'react';

import { Modal } from '@/components/Modal';
import { Agent } from '@/hooks/useAgents';

import { useTunnelManagement } from './useTunnelManagement';

interface RemoveTunnelModalProps {
  isOpen: boolean;
  onClose: () => void;
  agent: Agent;
  tunnelHostname: string;
  tunnelName?: string;
  onSuccess?: (taskId?: string) => void;
}

/**
 * Modal for removing a Cloudflare tunnel from an Aegis agent.
 *
 * Extracted from TunnelManagementTab to enable direct modal access from row actions
 * following information architecture best practices (colocation, reusability).
 *
 * Can be opened from:
 * - Row action "Remove Tunnel" (direct modal access)
 * - Tunnel tab "Remove Tunnel" button (within drawer)
 */
export const RemoveTunnelModal: React.FC<RemoveTunnelModalProps> = ({
  isOpen,
  onClose,
  agent,
  tunnelHostname,
  tunnelName,
  onSuccess,
}) => {
  const [isRemoving, setIsRemoving] = useState(false);
  const tunnelManagement = useTunnelManagement(isOpen);

  const handleRemove = async () => {
    if (!agent) return;

    try {
      setIsRemoving(true);

      const response = await tunnelManagement.removeTunnel(agent.client_id);

      onClose();
      onSuccess?.(response?.taskId);
    } catch (error) {
      console.error('Failed to remove tunnel:', error);
      // Error handling is managed by the tunnel management hook
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center gap-2">
          <ExclamationTriangleIcon className="text-chariot-status-critical size-5" />
          <span>Remove Cloudflare Tunnel</span>
        </div>
      }
      footer={{
        text: isRemoving ? 'Removing...' : 'Remove Tunnel',
        styleType: 'primary',
        onClick: handleRemove,
        disabled: isRemoving,
      }}
      data-testid="remove-tunnel-modal"
    >
      <div className="flex flex-col gap-3">
        <p className="text-chariot-text-primary">
          Are you sure you want to remove the tunnel from agent{' '}
          <strong>{agent.hostname}</strong>?
        </p>
        {tunnelName && (
          <p className="text-chariot-text-secondary text-sm">
            Tunnel: <span className="font-mono">{tunnelName}</span>
          </p>
        )}
        {tunnelHostname && (
          <p className="text-chariot-text-secondary text-sm">
            Hostname: <span className="font-mono">{tunnelHostname}</span>
          </p>
        )}
        <p className="text-chariot-text-secondary text-sm">
          This will disable secure SSH access to the agent.
        </p>
      </div>
    </Modal>
  );
};
